import { useIFetch } from '@/composables/useIFetch';
import apiRoutes from '@/const/apiRoutes';
import { getClientPromptSessionId } from '~/utils/clientMobileAppPrompt';
import { type RegisterStep } from '~/composables/useRegisterSteps';
import {
   stopPixelForMinor,
   trackEventAfterNavigation as trackPixelEventAfterNavigation,
   trackStep as trackPixelStep,
} from '~/composables/useMetaPixel';
import { pixelStepForRoute } from '~/utils/pixelCore';
import { isTeenTherapyType } from '~/utils/therapyType';
import type { AdConsent } from '~/types/AdConsent';
import type { AdConsentResult } from '~/composables/useAdConsent';
import type { AnonymousBodyInterface, AnonymousDataResponse } from '~/types/AnonymousDataResponse';
import type { ClientLoginInformation } from '~/types/ClientLoginInformation';
import type {
   ClientRegisterBodyInterface,
   ClientRegisterResponseInformation,
} from '~/types/ClientRegisterBodyInterface';
import type { CompanyVerifyBenefitCodeResponse } from '~/types/CompanyVerifyBenefitCodeResponse';
import type { InsuranceCoverageResponse } from '~/types/InsuranceCovergaeResponse';
import type { InsuranceVerifyBenefitCodeResponse } from '~/types/InsuranceVerifyBenefitCodeResponse';
import { stepForRoute, type AcquisitionStep, type ClientAcquisition } from '~/utils/acquisition';
import {
   browserAttachStorage,
   forgetAttachJourney,
   settleAttach,
   settledAttachReason,
} from '~/utils/acquisitionAttach';
import { isImpersonatingClientToken } from '~/utils/clientToken';
import { forgetVerificationEmail, rememberVerificationEmail } from '~/utils/verificationEmail';
const { isClientAuthenticated } = useAuthClientStorage();

export const useClientAuthStore = defineStore('clientAuth', () => {
   const { reset, track } = useSegment();
   const { languages } = storeToRefs(useSharedContentStore());
   const { setUser } = useSentry();
   const { setSnackbarDetail } = useSnackbarStore();
   const { store, clear, clientToken } = useAuthClientStorage();
   const { currentRegisterStep } = useRegisterSteps();
   const { clearAnonymous, storAnonymous, anonymousId, anonymousToken } = useAnonymousClientStorage();
   const acquisition = useAcquisition();
   const visitedStages = new Set<string>();
   let attachmentPending: Promise<string> | null = null;
   let attachmentAttempt = 0;
   const deferredAttachments = new Map<string, number>();
   const router = useRouter();
   const route = computed(() => useRoute());
   const { updateStatus } = useClientInfo();
   const { markSignupPending, markSignupAwaitingProfile } = useClientMobileAppPrompt();
   const anonymousData = ref<AnonymousDataResponse | null>(null);
   const companyBenefitSettings = ref<CompanyVerifyBenefitCodeResponse | null>(null);
   const coverageDetailBody = ref<InsuranceCoverageResponse | null>(null);
   const testingCode = ref<string | null>(null);
   const currentRegisterSteps: ComputedRef<RegisterStep[]> = computed(
      () => currentRegisterStep.value,
   );
   const previousStep = ref(-1);
   const getNextStep = (skipStep = false) => {
      const currentStepIndex =
         currentRegisterSteps.value.findIndex(
            (step: RegisterStep) => step.to.name === route.value.name,
         ) + (skipStep ? 1 : 0);
      previousStep.value = currentStepIndex;

      return currentRegisterSteps.value[currentStepIndex + 1] || null;
   };
   const getPreviousStep = () => {
      const currentStepIndex = currentRegisterSteps.value.findIndex(
         (step: RegisterStep) => step.to.name === route.value.name,
      );
      if (currentStepIndex === -1) return null;
      if (currentStepIndex === 0) return { to: { name: 'auth-client-login' } };
      return currentRegisterSteps.value[currentStepIndex - 1] || null;
   };

   const SignUpAsClient = async (detail: ClientRegisterBodyInterface) => {
      let { data, error } = await useIFetch<ClientRegisterResponseInformation>(
         apiRoutes?.client?.auth?.signup,
         {
            method: 'POST',
            body: { ...detail, anonymous_token: anonymousToken.value || undefined },
         },
      );
      if (!error?.value && data?.value) {
         track('Client Signup', {
            email: detail?.email,
            sponsor: anonymousData.value?.sponsor,
         });

         setSnackbarDetail({
            show: true,
            title: data?.value?.message,
            type: 'message',
         });
         clearAnonymous();
         rememberVerificationEmail(detail.email);
         router.push({ name: 'auth-client-register-verify-email' });
         anonymousData.value = null;
      }
      if (error?.value) {
         setSnackbarDetail({
            show: true,
            title: error?.value?.data?.message,
            type: 'error',
         });
      }
   };
   const loginAsClient = async (detail: { email: string; password: string }) => {
      const { data, error } = await useIFetch<ClientLoginInformation>(
         apiRoutes?.client?.auth?.signin,
         {
            method: 'POST',
            body: detail,
         },
      );

      if (!error?.value && data?.value?.token) {
         // Attachment uses the signed-in identity; failed login never consumes an offer.
         store(data.value.token);
         const attachment = await attachPendingRedemption();
         if (attachment === 'skipped' || attachment === 'applied') setSnackbarDetail({
            show: true,
            title: data?.value?.message,
            type: 'message',
         });
         setTimeout(() => {
            router.push('/client/booking');
         }, 500);
      }
      if (error?.value) {
         if (error?.value?.statusCode === 403) {
            await resendVerificationCode(detail.email);
            rememberVerificationEmail(detail.email);
            router.push({ name: 'auth-client-register-verify-email' });
         }
         setSnackbarDetail({
            show: true,
            title: error?.value?.data?.message,
            type: 'error',
         });
      }
   };
   const getAnonymousData = async () => {
      let { data, error } = await useIFetch<AnonymousDataResponse>(
         apiRoutes?.anonymous.get(anonymousId.value),
         { headers: anonymousToken.value ? { 'X-Anonymous-Token': anonymousToken.value } : {} },
      );
      if (!error?.value && data?.value) {
         anonymousData.value = data?.value;
         acquisition.importAttribution(data.value.acquisition);
         if (!isClientAuthenticated.value) updateStatus();
      }
      if (error?.value) {
         setSnackbarDetail({
            show: true,
            title: error?.value?.data?.message,
            type: 'error',
         });
      }
   };

   const createAnonymousId = async () => {
      let { data, error } = await useIFetch<{ id: string; owner_token?: string }>(apiRoutes?.anonymous.create, {
         method: 'POST',
         body: { attribution: acquisition.attribution.value },
      });
      if (!error?.value && data?.value) {
         storAnonymous(data?.value?.id, data.value.owner_token);
      }
      if (error?.value) {
         setSnackbarDetail({
            show: true,
            title: error?.value?.data?.message,
            type: 'error',
         });
      }
   };

   const updateAnonymousData = async (data: Partial<AnonymousBodyInterface>) => {
      const stage = data.acquisition?.current_step || (data.email ? 'email_captured' : stepForRoute(route.value.name));
      // Choosing Teen stops the pixel before this save can report anything.
      const choosesTeen = !!data.therapy_type_id &&
         !!useSharedContentStore().therapyTypes?.some(
            (type) => type.id === data.therapy_type_id && isTeenTherapyType(type.name),
         );
      if (choosesTeen) stopPixelForMinor();
      if (isClientAuthenticated?.value) {
         const { acquisition: acquisitionData, ...profileData } = data;
         const result = await useClientInfo().updateClientInfo(profileData);
         if (!result.error.value && stage) await acquisition.trackClientStep(stage, true);
         return !result.error.value;
      } else {
         if (!anonymousId.value) return false;
         const anonymousFields = { ...data };
         delete anonymousFields.acquisition;
         let { error } = await useIFetch(apiRoutes?.anonymous?.update(anonymousId.value), {
            method: 'PUT',
            headers: anonymousToken.value ? { 'X-Anonymous-Token': anonymousToken.value } : {},
            body: {
               ...anonymousFields,
               ...(anonymousToken.value ? { acquisition: {
                  current_step: stage,
                  completed: data.acquisition?.completed ?? true,
                  attribution: acquisition.attribution.value,
               } } : {}),
               language_id:
                  useCookie('i18n_redirected').value === 'en' ?
                     languages.value?.find((lang) => lang.name.toLowerCase() == 'english')?.id
                  :  languages.value?.find((lang) => lang.name.toLowerCase() == 'arabic')?.id,
            },
         });
         if (error?.value) {
            setSnackbarDetail({
               show: true,
               title: error?.value?.data?.message,
               type: 'error',
            });
            return false;
         }
         await getAnonymousData();
         // A saved answer reports its question and nothing about the answer. The therapy
         // type is named by the data because the auto-pick runs while the route is
         // still the previous one.
         trackPixelStep(data.therapy_type_id ? 'therapy_type' : pixelStepForRoute(route.value.name) ?? '');
         return true;
      }
   };

   const recordAcquisitionStep = async (step: AcquisitionStep, completed = false) => {
      if (isClientAuthenticated.value) return acquisition.trackClientStep(step, completed);
      // Recorded while the router is still moving, so the pixel waits for the page.
      if (step === 'signup_started') trackPixelEventAfterNavigation('SignupStarted');
      const stage = `${anonymousId.value}:${step}:${completed}`;
      if (!anonymousId.value || !anonymousToken.value || visitedStages.has(stage)) return;
      const { error } = await useIFetch(apiRoutes.anonymous.update(anonymousId.value), {
         method: 'PUT',
         headers: anonymousToken.value ? { 'X-Anonymous-Token': anonymousToken.value } : {},
         body: { acquisition: { current_step: step, completed } },
         key: `anonymous-progress-${stage}`,
      });
      if (!error.value) visitedStages.add(stage);
   };

   const consumeRedemptionHandoff = async (token: string) => {
      const { data, error } = await useIFetch<{
         id: string;
         owner_token: string;
         data: AnonymousDataResponse;
      }>('/anonymous/handoff', {
         method: 'POST',
         body: { token },
         key: 'anonymous-handoff',
      });
      if (error.value || !data.value?.id || !data.value.owner_token) {
         if (error.value) setSnackbarDetail({ show: true, title: error.value.data?.message, type: 'error' });
         return false;
      }
      storAnonymous(data.value.id, data.value.owner_token);
      anonymousData.value = data.value.data;
      deferredAttachments.clear();
      forgetAttachJourney(browserAttachStorage(), data.value.id);
      acquisition.importAttribution(data.value.data.acquisition);
      if (!isClientAuthenticated.value) {
         updateStatus();
         if (data.value.data?.acquisition?.promo?.code) promoAppliedNotice();
      }
      return true;
   };

   const promoAppliedNotice = () => {
      const { $i18n } = useNuxtApp();
      const key = 'acquisition.promoApplied';
      const title = $i18n.te(key) ? $i18n.t(key)
         : $i18n.locale.value === 'ar' ? 'تم تطبيق رمز الخصم بنجاح، قم بإعداد حسابك' : 'Promo code successfully applied, continue setting up your account.';
      setSnackbarDetail({ show: true, title, type: 'success', duration: 6000 });
   };

   const attachmentNotice = (key: string, english: string, arabic: string) => {
      const { $i18n } = useNuxtApp();
      const translated = () => {
         switch (key) {
            case 'acquisition.attachRetry': return $i18n.t('acquisition.attachRetry');
            case 'acquisition.attachDifferentAccount': return $i18n.t('acquisition.attachDifferentAccount');
            case 'acquisition.attachIneligible': return $i18n.t('acquisition.attachIneligible');
            case 'acquisition.attachInvalid': return $i18n.t('acquisition.attachInvalid');
            case 'acquisition.attachLimit': return $i18n.t('acquisition.attachLimit');
            default: return english;
         }
      };
      const title = $i18n.te(key) ? translated()
         : $i18n.locale.value === 'ar' ? arabic : english;
      setSnackbarDetail({ show: true, title, type: 'error' });
   };

   // Attaches the offer redeemed in this browser to the signed-in account.
   // Refusals that cannot change on retry are remembered per (account, journey)
   // in localStorage so a reload or a later sign-in neither retries nor repeats
   // the notice. The journey stays in the browser for the account it belongs to.
   const attachPendingRedemption = async (): Promise<string> => {
      const account = getClientPromptSessionId(clientToken.value?.access_token);
      if (!account || !isClientAuthenticated.value) return 'skipped';
      // An admin impersonating a client must not change that client's offers.
      if (isImpersonatingClientToken(clientToken.value?.access_token)) return 'skipped';
      if (!anonymousId.value || !anonymousToken.value) {
         clearAnonymous();
         anonymousData.value = null;
         return 'skipped';
      }
      if (attachmentPending) return attachmentPending;
      const id = anonymousId.value;
      const owner = anonymousToken.value;
      const storage = browserAttachStorage();
      const identityMatches = () => account === getClientPromptSessionId(clientToken.value?.access_token) &&
         anonymousId.value === id && anonymousToken.value === owner;
      const attemptKey = `${account}:${id}`;
      if (settledAttachReason(storage, account, id)) return 'skipped';
      if ((deferredAttachments.get(attemptKey) || 0) > Date.now()) return 'retry';
      const settle = (reason: Parameters<typeof settleAttach>[3]) => {
         settleAttach(storage, account, id, reason);
         // Covers storage that is blocked or full for the rest of this page load.
         deferredAttachments.set(attemptKey, Number.POSITIVE_INFINITY);
      };
      const clearCompletedJourney = () => {
         if (!identityMatches()) return;
         clearAnonymous();
         anonymousData.value = null;
         deferredAttachments.delete(attemptKey);
      };
      const retryNotice = () => attachmentNotice('acquisition.attachRetry',
         'You are signed in. Your offer could not be checked yet; it is saved and will be retried.',
         'تم تسجيل دخولك. لم نتمكن من التحقق من العرض بعد؛ تم حفظه وسنعيد المحاولة.');
      attachmentPending = (async () => {
         try {
            await acquisition.refreshClientAcquisition();
            if (!identityMatches()) return 'retry';
            // The server decides whether this account may still use the offer,
            // including accounts that paid before; an ineligible offer comes
            // back as the 'ineligible' outcome.
            // Converted journeys cannot be read anonymously; replay the owned attach directly after reload.
            const { data, error } = await useIFetch<{
               attached: boolean;
               outcome: 'applied' | 'ineligible' | 'invalid' | 'no_offer';
               acquisition: ClientAcquisition;
            }>('/client/acquisition/attach', {
               method: 'POST',
               body: { anonymous_id: id, owner_token: owner },
               key: `acquisition-attach-${account}-${id}-${++attachmentAttempt}`,
            });
            if (!identityMatches()) return 'retry';
            if (error.value || !data.value?.attached) {
               const status = error.value?.statusCode;
               if (status === 406) {
                  settle('mismatch');
                  attachmentNotice('acquisition.attachDifferentAccount',
                     'This offer belongs to another email. Sign in with the email used on the redeem page to continue.',
                     'هذا العرض مرتبط ببريد إلكتروني آخر. سجّل الدخول بالبريد المستخدم في صفحة استرداد العرض للمتابعة.');
                  return 'mismatch';
               }
               if (status === 409) {
                  settle('limit');
                  attachmentNotice('acquisition.attachLimit',
                     'You are signed in. Your account cannot save another offer right now; review the prices shown.',
                     'تم تسجيل دخولك. لا يمكن لحسابك حفظ عرض آخر حاليًا؛ راجع الأسعار المعروضة.');
                  await acquisition.refreshClientAcquisition();
                  return 'retry';
               }
               // The journey belongs to another account or can no longer be
               // attached to this one; retrying cannot succeed.
               if (status === 403 || status === 404) {
                  settle('unavailable');
                  return 'skipped';
               }
               deferredAttachments.set(attemptKey, Date.now() + 30000);
               retryNotice();
               await acquisition.refreshClientAcquisition();
               return 'retry';
            }
            clearCompletedJourney();
            await acquisition.refreshClientAcquisition();
            if (account !== getClientPromptSessionId(clientToken.value?.access_token)) return 'retry';
            if (data.value.outcome === 'ineligible') {
               attachmentNotice('acquisition.attachIneligible',
                  'You are signed in. This offer is not eligible for your account; review the prices shown.',
                  'تم تسجيل دخولك. حسابك غير مؤهل لهذا العرض؛ راجع الأسعار المعروضة.');
               return 'rejected';
            }
            if (data.value.outcome === 'invalid') {
               attachmentNotice('acquisition.attachInvalid',
                  'You are signed in. This offer is no longer available; review the prices shown.',
                  'تم تسجيل دخولك. هذا العرض لم يعد متاحًا؛ راجع الأسعار المعروضة.');
               return 'rejected';
            }
            if (data.value.outcome === 'applied') {
               promoAppliedNotice();
               return 'applied';
            }
            return 'skipped';
         } catch {
            if (identityMatches()) {
               deferredAttachments.set(attemptKey, Date.now() + 30000);
               retryNotice();
            }
            return 'retry';
         }
      })();
      try { return await attachmentPending; } finally { attachmentPending = null; }
   };

   const verifyCompanyBenefitCode = async (body: {
      benefit_code: string;
      email: string;
      country_id: string;
   }) => {
      let { data, error } = await useIFetch<CompanyVerifyBenefitCodeResponse>(
         apiRoutes?.company?.verify,
         {
            method: 'POST',
            headers: anonymousToken.value ? { 'X-Anonymous-Token': anonymousToken.value } : {},
            body: {
               ...body,
               anonymous_id: anonymousId.value,
               anonymous_token: anonymousToken.value || undefined,
            },
         },
      );
      if (data.value) {
         companyBenefitSettings.value = {
            ...data.value,
            benefit_code: body.benefit_code,
         };
         useSharedContentStore().therapyTypes = data?.value.therapy_types;
      }
      return { data, error };
   };
   const getCoverageDetail = async () => {
      let { data, error } = await useIFetch<InsuranceCoverageResponse>(
         apiRoutes?.client?.account?.coverage,
      );

      if (data?.value) {
         coverageDetailBody.value = data?.value;
         if (data?.value?.coverage?.therapy_types?.length) {
            useSharedContentStore().therapyTypes =
               useSharedContentStore().therapyTypes?.filter((item: item) =>
                  data?.value?.coverage?.therapy_types?.includes(item.name),
               ) || [];
         }
         return data.value;
      }
      if (error.value) {
         setSnackbarDetail({
            show: true,
            title: error.value.data.message,
            type: 'error',
         });
         return error.value;
      }
   };

   const verifyInsuranceBenefitCode = async (body: InsuranceVerifyBenefitCodeResponse) => {
      let { data, error } = await useIFetch<CompanyVerifyBenefitCodeResponse>(
         apiRoutes?.insurance?.verify,
         {
            method: 'POST',
            headers: anonymousToken.value ? { 'X-Anonymous-Token': anonymousToken.value } : {},
            body: {
               ...body,
               anonymous_id: anonymousId.value,
               anonymous_token: anonymousToken.value || undefined,
            },
         },
      );
      if (error.value) {
         setSnackbarDetail({
            show: true,
            title: error.value.data?.verification?.message || error.value.data.message,
            type: 'error',
         });
      }
      return { data, error };
   };
   const verifiedEmail = async (email: string, code: string) => {
      let { data, error } = await useIFetch(apiRoutes?.client?.auth.verifyToken, {
         method: 'POST',
         body: {
            action: 'activate_account',
            email: email,
            token: code,
         },
      });
      if (data?.value) {
         setSnackbarDetail({
            show: true,
            title: (data.value as { message: string })?.message,
            type: 'message',
         });
         forgetVerificationEmail();
         const verifiedToken = (data?.value as { token: IToken })?.token;
         const signupSessionId = getClientPromptSessionId(verifiedToken?.access_token);
         if (signupSessionId) markSignupAwaitingProfile(signupSessionId);
         store(verifiedToken);
         await acquisition.refreshClientAcquisition();

         // Fetch profile to get the correct sponsor (anonymous data is cleared after signup)
         const { getClientInfo, clientProfile } = useClientInfo();
         const profileRefreshed = await getClientInfo({ silent: true });
         const verifiedClientId = useClientInfo().clientProfile?.id;
         if (profileRefreshed && verifiedClientId) {
            markSignupPending(verifiedClientId);
         }

         track('Client Verified', {
            email: email,
            sponsor: clientProfile?.sponsor || 'self',
         });

         setTimeout(async () => {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const { updateTimeZone } = useTimeZoneStore();
            await updateTimeZone(timeZone, true, true);
            router.push('/client/booking');
         }, 500);
      }
      if (error?.value) {
         setSnackbarDetail({
            show: true,
            title: error.value.data.message,
            type: 'error',
         });
         return error.value;
      }
   };
   const resendVerificationCode = async (email: string) => {
      const { data, error } = await useIFetch(apiRoutes?.client?.auth.resendVerifyToken, {
         method: 'POST',
         body: {
            action: 'activate_account',
            email: email,
         },
      });
      if (data.value) {
         setSnackbarDetail({
            show: true,
            title: (data.value as { message: string })?.message,
            type: 'message',
         });
         testingCode.value = (data.value as { token: string })?.token;
      }
      if (error.value) {
         setSnackbarDetail({
            show: true,
            title: error.value.data.message,
            type: 'error',
         });
      }
      return { data, error };
   };
   const logoutAsClient = () => {
      reset();
      setUser(null);
      clear();
      location.reload();
   };
   const forgetPasswordAsClient = async (email: string) => {
      let { data, error } = await useIFetch(apiRoutes?.client?.auth.forgetPassword, {
         method: 'POST',
         body: {
            email,
         },
      });
      if (data?.value)
         setSnackbarDetail({
            show: true,
            title: (data.value as { message: string })?.message,
            type: 'message',
         });
      if (error?.value)
         setSnackbarDetail({
            show: true,
            title: error.value.data.message,
            type: 'error',
         });
      return { data, error };
   };
   const resetPasswordAsClient = async (dataObject: {
      email: string;
      new_password: string;
      confirm_password: string;
      token: string;
   }) => {
      let { data, error } = await useIFetch<{
         client_id: string;
         message: string;
         token: {
            access_token: string;
            refresh_token: string;
         };
      }>(apiRoutes?.client?.auth.resetPassword, {
         method: 'POST',
         body: dataObject,
      });
      if (data?.value) {
         setSnackbarDetail({
            show: true,
            title: (data.value as { message: string })?.message,
            type: 'message',
         });
         store(data.value.token);
         setTimeout(() => {
            router.push('/client/booking');
         }, 500);
      }
      if (error?.value)
         setSnackbarDetail({
            show: true,
            title: error.value.data.message,
            type: 'error',
         });
      return { data, error };
   };
   const resendTokenAsClient = async (email: string, action: string) => {
      let { data, error } = await useIFetch<{ message: string }>(
         apiRoutes?.client?.auth.resendVerifyToken,
         {
            method: 'POST',
            body: {
               action,
               email,
            },
         },
      );
      if (data.value)
         setSnackbarDetail({
            show: true,
            title: data.value.message,
            type: 'message',
         });

      if (error.value)
         setSnackbarDetail({
            show: true,
            title: error.value.data.message,
            type: 'error',
         });
      return { data, error };
   };
   // The signed-out visitor's advertising choice, kept on their own journey so signup
   // carries it to the account. The server answers 404 for a journey that is not
   // owned by this token, which the prompt treats as "do not ask, do not track".
   // GET and PUT share a URL, so each call gets its own key or they would share
   // (and cancel) one asyncData entry.
   const JOURNEY_AD_CONSENT_SAVE_TIMEOUT_MS = 15000;
   let journeyAdConsentCall = 0;
   const journeyAdConsent = async (method: 'GET' | 'PUT', granted?: boolean): Promise<AdConsentResult> => {
      const id = anonymousId.value;
      const owner = anonymousToken.value;
      if (!id || !owner) return { consent: null, error: { status: 404 } };
      const { data, error } = await useIFetch<AdConsent>(apiRoutes.anonymous.adConsent(id), {
         method,
         key: `journey-ad-consent-${method.toLowerCase()}-${id}-${++journeyAdConsentCall}`,
         headers: { 'X-Anonymous-Token': owner },
         ...(method === 'PUT' ? { body: { granted }, timeout: JOURNEY_AD_CONSENT_SAVE_TIMEOUT_MS } : {}),
      });
      return {
         consent: data.value ?? null,
         error: error.value ? { status: error.value.statusCode } : null,
      };
   };
   const getJourneyAdConsent = () => journeyAdConsent('GET');
   const setJourneyAdConsent = (granted: boolean) => journeyAdConsent('PUT', granted);
   const getLoggedTherapyTypes = async () => {
      const { data, error } = await useIFetch<item[]>(apiRoutes.client.benefitCode.getTherapyTypes);
      if (data?.value) {
         useSharedContentStore().therapyTypes = data?.value;
      }
      if (error?.value) {
         setSnackbarDetail({
            show: true,
            title: error?.value?.data?.message,
            type: 'error',
         });
      }
   };
   return {
      loginAsClient,
      SignUpAsClient,
      getAnonymousData,
      anonymousData,
      anonymousId,
      anonymousToken,
      getJourneyAdConsent,
      setJourneyAdConsent,
      logoutAsClient,
      createAnonymousId,
      updateAnonymousData,
      currentRegisterSteps,
      getNextStep,
      verifyCompanyBenefitCode,
      companyBenefitSettings,
      getPreviousStep,
      verifiedEmail,
      resendVerificationCode,
      testingCode,
      coverageDetailBody,
      getCoverageDetail,
      verifyInsuranceBenefitCode,
      forgetPasswordAsClient,
      resetPasswordAsClient,
      resendTokenAsClient,
      previousStep,
      getLoggedTherapyTypes,
      recordAcquisitionStep,
      consumeRedemptionHandoff,
      attachPendingRedemption,
   };
});
